<template>
  <div class="theater-tax">
    <div class="theater-tax__row">
      <v-switch
        :input-value="taxEnabled"
        :disabled="disabled"
        hide-details
        inset
        class="theater-tax__switch"
        @change="handleToggle"
      />
      <div class="theater-tax__content">
        <div class="theater-tax__text">
          <span class="theater-tax__title">{{ $adminT('performances.tax', 'Add tax class') }}</span>
          <p class="theater-tax__subtitle">
            {{ $adminT('performances.taxHint', 'Assign a tax class that will apply to tickets for this event.') }}
          </p>
        </div>
        <div v-if="taxEnabled" class="theater-tax__options">
          <div v-for="rate in rates" :key="rate.value" class="theater-tax__option">
            <label class="theater-tax__radio" :for="`theater-vat-${uid}-${rate.value}`">
              <input
                :id="`theater-vat-${uid}-${rate.value}`"
                type="radio"
                :name="`theater-vat-${uid}`"
                :value="rate.value"
                :checked="isSelected(rate.value)"
                :disabled="disabled"
                @change="$emit('input', rate.value)"
              >
              <v-icon size="24" :color="isSelected(rate.value) ? '#0277BD' : '#666666'">
                {{ isSelected(rate.value) ? 'radio_button_checked' : 'radio_button_unchecked' }}
              </v-icon>
              <span class="theater-tax__rate">{{ workflowLabel(rate.value) }}%</span>
            </label>
            <p class="theater-tax__description">{{ workflowLabel(rate.description) }}</p>
          </div>
        </div>
        <div v-if="taxEnabled" class="theater-tax__note">
          <v-icon size="24" color="#0277BD">info_outline</v-icon>
          <p>{{ workflowLabel(note) }}</p>
        </div>
        <p v-if="disabled" class="theater-tax__hint">
          {{ $adminT('performances.taxLocked', 'The tax class cannot be changed after tickets have been sold.') }}
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import WorkflowDisplay from '@/admin-i18n/workflows-all-display'

const DEFAULT_VAT_RATE = 5

export default {
  mixins: [WorkflowDisplay],
  name: 'theater-add-tax-panel',
  props: {
    value: {
      type: [Number, String],
      default: null,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    rates() {
      return [
        { value: 5, description: this.$adminT('performances.tax5', '— for concerts, festivals, movie projections, and sports and culture events') },
        { value: 13, description: this.$adminT('performances.tax13', '— for catering/food services and accommodation related to the event') },
        { value: 25, description: this.$adminT('performances.tax25', '— for other events') },
      ]
    },
    note() {
      return this.$adminT('performances.taxNote', 'Note: Tax rates depend on the specific nature of your event and may vary. Please consult your accountant or the Tax Administration to confirm the correct rate before publishing your event.')
    },
    taxEnabled() {
      return this.value != null && Number(this.value) > 0
    },
    // Keeps the radio ids/name unique when more than one picker is on the page.
    uid() {
      return this._uid
    },
  },
  methods: {
    isSelected(rate) {
      return this.taxEnabled && Number(this.value) === rate
    },
    handleToggle(checked) {
      this.$emit('input', checked ? DEFAULT_VAT_RATE : 0)
    },
  },
}
</script>

<style scoped>
/* Card matches the performance cards above it. */
.theater-tax {
  margin-top: 2rem;
  max-width: 684px;
  box-sizing: border-box;
  padding: 2rem 1.5rem;
  border: solid 2px #d9d9d9;
  border-radius: 8px;
}
.theater-tax__row {
  display: flex;
  align-items: flex-start;
  gap: 1.25rem;
}
.theater-tax__switch {
  margin: 0;
  padding: 0;
  flex: 0 0 auto;
}
.theater-tax__content {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}
.theater-tax__text {
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
  padding-top: 0.125rem;
}
.theater-tax__title {
  font-family: 'Raleway';
  font-style: normal;
  font-feature-settings: 'pnum' on, 'lnum' on;
  font-weight: 700;
  font-size: 1rem;
  line-height: 1.2;
  text-transform: uppercase;
  color: #666666;
}
.theater-tax__subtitle {
  font-family: 'Source Sans Pro';
  font-style: normal;
  font-weight: 400;
  font-size: 1rem;
  line-height: 1.5;
  color: #666666;
  margin: 0;
}
.theater-tax__options {
  display: flex;
  flex-direction: column;
  gap: 0.625rem;
}
.theater-tax__option {
  display: flex;
  align-items: flex-start;
  gap: 0.625rem;
}
.theater-tax__radio {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  flex: 0 0 auto;
  cursor: pointer;
}
.theater-tax__radio input[type='radio'] {
  /* Kept in the layer for keyboard focus, but the icon is what gets rendered. */
  position: absolute;
  width: 1px;
  height: 1px;
  opacity: 0;
  pointer-events: none;
}
.theater-tax__radio input[type='radio']:disabled ~ * {
  cursor: default;
}
.theater-tax__rate {
  font-family: 'Source Sans Pro';
  font-style: normal;
  font-weight: 400;
  font-size: 1.125rem;
  line-height: 1.2;
  color: #666666;
  white-space: nowrap;
}
.theater-tax__description {
  font-family: 'Source Sans Pro';
  font-style: normal;
  font-weight: 400;
  font-size: 1rem;
  line-height: 1.5;
  color: #666666;
  margin: 0;
}
.theater-tax__note {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  padding: 0.75rem;
  border-radius: 0.5rem;
  background: #e6f1f8;
}
.theater-tax__note p {
  font-family: 'Source Sans Pro';
  font-style: normal;
  font-weight: 400;
  font-size: 0.875rem;
  line-height: 1.2;
  color: #0277bd;
  margin: 0;
}
.theater-tax__hint {
  font-family: 'Source Sans Pro';
  font-size: 0.875rem;
  color: #979797;
  margin: 0;
}
</style>
